import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { useAuth } from "../AuthProvider";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { Text } from "../text";
import { Subheading } from "../heading";
import { Badge } from "../badge";
import { Divider } from "../divider";
import {
  TicketIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const statusLabels = {
  open: "Abierto",
  closed: "Cerrado",
  needs_attention: "En Atención",
};

const statusColors = {
  open: "blue",
  closed: "green",
  needs_attention: "amber",
};

function formatDate(ts) {
  if (!ts) return "-";
  const date = ts.toDate
    ? ts.toDate()
    : new Date(ts.seconds ? ts.seconds * 1000 : ts);
  return date.toLocaleString();
}

const TicketCard = ({ ticket, muted }) => (
  <div
    className={`rounded-xl border bg-white p-5 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-800/50 ${
      muted
        ? "border-zinc-200 opacity-75"
        : "border-zinc-200"
    }`}
    style={!muted ? { borderLeft: `4px solid ${ACCENT}` } : {}}
  >
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="font-semibold text-zinc-900 dark:text-white">
        {ticket.subject}
      </span>
      <Badge color={statusColors[ticket.status] || "zinc"}>
        {statusLabels[ticket.status] || ticket.status}
      </Badge>
    </div>
    <Text className="mb-3">{ticket.message}</Text>
    <div className="flex flex-wrap gap-x-4 text-xs text-zinc-400 dark:text-zinc-500">
      <span>Creado: {formatDate(ticket.createdAt)}</span>
      <span>Actualizado: {formatDate(ticket.updatedAt)}</span>
    </div>
    {ticket.adminComment && (
      <div
        className="mt-3 flex gap-2.5 rounded-lg p-3"
        style={{ backgroundColor: ACCENT + "0d", borderLeft: `3px solid ${ACCENT}` }}
      >
        <ChatBubbleLeftIcon
          className="mt-0.5 h-4 w-4 shrink-0"
          style={{ color: ACCENT }}
        />
        <Text className="!mt-0">
          <strong>Respuesta:</strong> {ticket.adminComment}
        </Text>
      </div>
    )}
  </div>
);

const UserTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        // Get user's ticket IDs
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const ticketIds = userDoc.data()?.tickets || [];

        if (ticketIds.length === 0) {
          setTickets([]);
          setLoading(false);
          return;
        }

        // Fetch tickets by IDs (Firestore 'in' query max 10)
        const ticketsRef = collection(db, "ticketsUsuarios");
        let fetchedTickets = [];
        for (let i = 0; i < ticketIds.length; i += 10) {
          const q = query(
            ticketsRef,
            where("__name__", "in", ticketIds.slice(i, i + 10))
          );
          const ticketDocs = await getDocs(q);
          fetchedTickets = fetchedTickets.concat(
            ticketDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );
        }
        setTickets(
          fetchedTickets.sort(
            (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
          )
        );
      } catch (err) {
        setTickets([]);
      }
      setLoading(false);
    };

    fetchTickets();
  }, [user.uid]);

  const activeTickets = tickets.filter((t) => t.status !== "closed");
  const resolvedTickets = tickets.filter((t) => t.status === "closed");

  return (
    <div>
      {/* Active tickets */}
      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: ACCENT + "1a" }}
        >
          <TicketIcon className="h-4 w-4" style={{ color: ACCENT }} />
        </div>
        <Subheading level={2}>Tickets Activos</Subheading>
      </div>

      {loading ? (
        <Text>Cargando...</Text>
      ) : activeTickets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center dark:border-zinc-600">
          <Text>No tienes tickets activos.</Text>
        </div>
      ) : (
        <div className="space-y-4">
          {activeTickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} />
          ))}
        </div>
      )}

      <Divider soft className="my-8" />

      {/* Resolved tickets */}
      <div className="flex items-center gap-2.5 mb-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-700/50">
          <CheckCircleIcon className="h-4 w-4 text-zinc-400" />
        </div>
        <Subheading level={2}>Tickets Resueltos</Subheading>
      </div>

      {loading ? (
        <Text>Cargando...</Text>
      ) : resolvedTickets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center dark:border-zinc-600">
          <Text>No tienes tickets resueltos.</Text>
        </div>
      ) : (
        <div className="space-y-4">
          {resolvedTickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} muted />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserTickets;
